import dayjs from "dayjs";
import { z } from "zod";
import type { UcccOfdAgentInfo } from "./ucccOfdShipmentInformation.type";

/**
 * TypeScript types and Zod schemas for UC/CC OFD shipment data.
 * Includes validation for grouped list response, individual shipment rows,
 * and dashboard summary data with proper date transformations.
 */

export const ucccOfdShipmentsSchema = (() => {
  /** Parse as UTC so `.format()` matches backend calendar date/time (no local shift). */
  const dayjsUtcRequired = z
    .union([z.string(), z.date()])
    .transform((val) => dayjs.utc(val))
    .refine((d) => d.isValid(), { message: "Invalid date" });

  const dayjsUtcOptionalNullable = z
    .union([z.string(), z.date(), z.null()])
    .optional()
    .transform((val) => {
      if (val === null || val === undefined) return null;
      const d = dayjs.utc(val);
      return d.isValid() ? d : null;
    });

  /** API may send null for lhRegionId on SIC objects; normalize for the client. */
  const sic = z.object({
    id: z.number(),
    code: z.string(),
    ownership: z.string(),
    lhRegionId: z.preprocess(
      (val) => (val === null || val === undefined ? 0 : val),
      z.number()
    ),
    timeZone: z.string(),
  });

  const consignee = z.object({
    id: z.number(),
    name: z.string(),
    address: z.string(),
    city: z.string(),
    state: z.string(),
    zipCode: z.string(),
  });

  /** Parses agent SIC – can arrive as null, a plain string, or a SIC-like object. */
  const agentSic = z
    .unknown()
    .nullable()
    .optional()
    .transform((val): UcccOfdAgentInfo | null => {
      if (val === null || val === undefined) return null;
      if (typeof val === "string") return { sic: val, carrierName: null };
      if (typeof val === "object") {
        const obj = val as Record<string, unknown>;
        const sicCandidate =
          obj.code ?? obj.sicCode ?? obj.agentCode ?? obj.name ?? null;
        const sic = typeof sicCandidate === "string" ? sicCandidate : null;
        const carrierNameRaw = obj.carrierName ?? null;
        const carrierName =
          typeof carrierNameRaw === "string" ? carrierNameRaw : null;
        return { sic, carrierName };
      }
      return null;
    });

  const row = z
    .object({
      shipmentId: z.number(),
      proNumber: z.string(),
      pickupDateUtc: dayjsUtcRequired,
      dueDate: dayjsUtcRequired,
      deliveryDateUtc: dayjsUtcOptionalNullable,
      appointmentStartTimestampUtc: dayjsUtcOptionalNullable,
      originSic: sic,
      originAgent: agentSic,
      destinationSic: sic,
      destinationAgent: agentSic,
      currentSic: sic.nullable(),
      finalDestination: z.string(),
      pts: z.number(),
      pcs: z.number(),
      weight: z.number(),
      shipmentTags: z.array(z.string()),
      status: z.string(),
      /** FB follow-up label from API (e.g. EXPEDITE, SERV FAILURE); merged into `fbFollowUpStatus` when not overridden. */
      followUpStatus: z.string().nullable().optional(),
      tripNumber: z.number().nullable(),
      etaUtc: dayjsUtcOptionalNullable,
      ataUtc: dayjsUtcOptionalNullable,
      etaStatus: z
        .string()
        .nullable()
        .optional()
        .transform((v) => v ?? null),
      locationDetails: z.unknown().nullable().optional(),
      consignee,
      /** Optional client id; defaults to `String(shipmentId)` after parse. */
      id: z.string().optional(),
      noDimensions: z.boolean().optional(),
      /** Real API field — true when a service failure has been recorded for this shipment. */
      isServiceFailure: z.boolean().nullable().optional(),
      /** Real API field — true when the shipment has an active expedite. */
      isExpedite: z.boolean().nullable().optional(),
      serviceFailure: z.boolean().optional(),
      expediteShipment: z.boolean().optional(),
      expediteCreatedOn: dayjsUtcOptionalNullable,
      serviceFailureCreatedOn: dayjsUtcOptionalNullable,
      hasProWarning: z.boolean().optional(),
      hasProExpedite: z.boolean().optional(),
      hasProServiceFailure: z.boolean().optional(),
      deliveryDate: z.string().optional(),
      arrivalToSic: z.string().optional(),
      arrivalToSicStatus: z.enum(["LATE", "ON TIME"]).optional(),
      appt: z.string().optional(),
      fbFollowUpStatus: z.string().optional(),
      currentZone: sic.optional(),
    })
    .transform((r) => {
      const followUpFromApi = r.followUpStatus ?? null;
      const trimmedFollowUp =
        followUpFromApi && followUpFromApi.trim() !== ""
          ? followUpFromApi.trim()
          : null;
      /** Match dashboard option label; API uses `"Pending POD"` or legacy `PendingPod`. */
      const fbStatusFromFollowUp =
        trimmedFollowUp === "Pending POD" || trimmedFollowUp === "PendingPod"
          ? "PENDING POD"
          : trimmedFollowUp;
      const fbFollowUpStatus =
        r.fbFollowUpStatus ?? fbStatusFromFollowUp ?? undefined;
      const expediteFromFollowUp =
        trimmedFollowUp !== null &&
        trimmedFollowUp.toUpperCase() === "EXPEDITE";
      const serviceFailureFromFollowUp =
        trimmedFollowUp !== null &&
        trimmedFollowUp.toUpperCase() === "SERV FAILURE";
      return {
        ...r,
        id: r.id ?? String(r.shipmentId),
        locationDetails: r.locationDetails ?? null,
        followUpStatus: followUpFromApi,
        fbFollowUpStatus,
        isExpedite: r.isExpedite ?? false,
        isServiceFailure: r.isServiceFailure ?? false,
        hasProExpedite:
          r.hasProExpedite ?? r.isExpedite ?? expediteFromFollowUp,
        hasProServiceFailure:
          r.hasProServiceFailure ??
          r.isServiceFailure ??
          r.serviceFailure ??
          serviceFailureFromFollowUp,
      };
    });

  const ownerGroup = z.object({
    ownerName: z.string(),
    ownerId: z.string(),
    shipmentCount: z.number(),
    shipments: z.array(row),
  });

  const listResponse = z.object({
    unassignedShipmentCount: z.number(),
    unassignedShipments: z.array(row),
    ownerGroups: z.array(ownerGroup),
  });

  return { row, listResponse, ownerGroup };
})();

export type UcccOfdShipmentRow = z.infer<typeof ucccOfdShipmentsSchema.row>;

export type UcccOfdShipmentsListResponse = z.infer<
  typeof ucccOfdShipmentsSchema.listResponse
>;

/** All shipment rows across unassigned pool and owner-assigned groups (unique `id` / `shipmentId`). */
export const flattenUcccOfdShipmentsList = (
  list: UcccOfdShipmentsListResponse
): UcccOfdShipmentRow[] => [
  ...list.unassignedShipments,
  ...list.ownerGroups.flatMap((g) => g.shipments),
];

// --- UC/CC OFD dashboard-summary API ---

export const ucccOfdDashboardSummarySchema = z.object({
  totalUcCcShipments: z.number(),
  serviceFailureCount: z.number(),
  deliveredCount: z.number(),
  onTimePerformance: z.object({
    percentage: z.number(),
    onTimeCount: z.number(),
    total: z.number(),
  }),
  followUpStatuses: z.object({
    delivered: z.number(),
    expedite: z.number(),
    ofd: z.number(),
    osd: z.number(),
    pendingAppt: z.number(),
    pendingPod: z.number(),
    serviceFailure: z.number(),
    noStatus: z.number(),
  }),
});

export type UcccOfdDashboardSummaryData = z.infer<
  typeof ucccOfdDashboardSummarySchema
>;

export type UcccOfdFbFollowUpStatusCounts =
  UcccOfdDashboardSummaryData["followUpStatuses"];

export const EMPTY_UCCC_OFD_DASHBOARD_SUMMARY: UcccOfdDashboardSummaryData = {
  totalUcCcShipments: 0,
  serviceFailureCount: 0,
  deliveredCount: 0,
  onTimePerformance: {
    percentage: 0,
    onTimeCount: 0,
    total: 0,
  },
  followUpStatuses: {
    delivered: 0,
    expedite: 0,
    ofd: 0,
    osd: 0,
    pendingAppt: 0,
    pendingPod: 0,
    serviceFailure: 0,
    noStatus: 0,
  },
};
